import React, { useEffect, useRef, useState } from 'react';

declare const maplibregl: typeof import('maplibre-gl');

interface Pin {
  id: string; label: string; brigadista: string;
  lat: number; lng: number;
  categoria: { slug: string; label: string; icon: string; color: string };
}
interface Props {
  pins?: Pin[]; onPinClick?: (id: string) => void;
  catActiva?: string | null; show3D?: boolean;
  instSlug?: string | null; nocturno?: boolean;
}

const LIGHT = {
  fondo: '#dde6f5', agua: '#bbd8f5', edificio: '#c8d5ed',
  via: '#ffffff', limite: '#a78bfa', viaTexto: '#9ca3af',
  viaHalo: '#dde6f5', lugarTexto: '#374151', lugarHalo: '#dde6f5',
};
const DARK = {
  fondo: '#0d1424', agua: '#0a1020', edificio: '#1a2640',
  via: '#2a3850', limite: '#7c3aed', viaTexto: '#4a6080',
  viaHalo: '#0d1424', lugarTexto: '#8ba4c4', lugarHalo: '#0d1424',
};

// Coordenadas por institución / vista predeterminada
const VISTAS: Record<string, { center: [number, number]; zoom: number; pitch: number }> = {
  todos:     { center: [-66.0, 8.0],     zoom: 5.5,  pitch: 0  },  // Venezuela completa
  default:   { center: [-66.916, 10.488], zoom: 12.5, pitch: 30 },  // Caracas
  // Se pueden agregar parroquias específicas:
  'el-paraiso': { center: [-66.937, 10.483], zoom: 15, pitch: 45 },
  'la-vega':    { center: [-66.937, 10.467], zoom: 15, pitch: 45 },
  'antimano':   { center: [-66.955, 10.463], zoom: 15, pitch: 45 },
  'catia':      { center: [-66.945, 10.493], zoom: 15, pitch: 45 },
  '23-de-enero':{ center: [-66.950, 10.480], zoom: 15, pitch: 45 },
};


const CAT_COLORS: Record<string, string> = {
  salud: '#a78bfa', educ: '#2563eb', espacios: '#059669',
  vialidad: '#dc2626', servicios: '#ca8a04', telecom: '#0ea5e9',
  ambiente: '#16a34a', establecimientos: '#d97706',
};
const CAT_ICONS: Record<string, string> = {
  salud: '🏥', educ: '🏫', espacios: '🌳',
  vialidad: '🚧', servicios: '💡', telecom: '📡',
  ambiente: '🌿', establecimientos: '🏢',
};

// Genera una imagen de emoji sobre círculo de color para usar como ícono de mapa
function makeEmojiImage(emoji: string, bg: string, size = 52) {
  const canvas = document.createElement('canvas');
  canvas.width = size; canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  // Sombra
  ctx.shadowColor = 'rgba(0,0,0,0.35)';
  ctx.shadowBlur = 6;
  ctx.shadowOffsetY = 2;
  // Círculo de fondo
  ctx.beginPath();
  ctx.arc(size / 2, size / 2, size / 2 - 3, 0, Math.PI * 2);
  ctx.fillStyle = bg;
  ctx.fill();
  // Borde blanco
  ctx.shadowColor = 'transparent';
  ctx.strokeStyle = '#fff';
  ctx.lineWidth = 3;
  ctx.stroke();
  // Emoji
  ctx.font = `${Math.round(size * 0.45)}px serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(emoji, size / 2, size / 2 + 1);
  return ctx.getImageData(0, 0, size, size);
}

function toGeoJSON(pins: Pin[], catActiva: string | null | undefined) {
  const filtered = catActiva ? pins.filter(p => p.categoria.slug === catActiva) : pins;
  return {
    type: 'FeatureCollection' as const,
    features: filtered.map(pin => ({
      type: 'Feature' as const,
      geometry: {
        type: 'Point' as const,
        coordinates: [pin.lng, pin.lat],
      },
      properties: {
        id: pin.id,
        label: pin.label,
        brigadista: pin.brigadista,
        catLabel: pin.categoria.label,
        slug: pin.categoria.slug,
      },
    })),
  };
}



function buildStyle(sources: Record<string, any>, glyphs: string) {
  const tieneCalles = !!sources.openmaptiles;
  return {
    version: 8 as const,
    glyphs,
    sources,
    layers: [
      { id: 'fondo', type: 'background' as const, paint: { 'background-color': '#dde6f5' } },
      ...(tieneCalles ? [
        { id: 'agua', type: 'fill' as const, source: 'openmaptiles', 'source-layer': 'water', paint: { 'fill-color': '#bbd8f5' } },
        {
          id: 'edificio-3d', type: 'fill-extrusion' as const, source: 'openmaptiles',
          'source-layer': 'building', minzoom: 14,
          paint: {
            'fill-extrusion-color': '#c8d5ed',
            'fill-extrusion-height': ['coalesce', ['get', 'render_height'], 6] as any,
            'fill-extrusion-base': ['coalesce', ['get', 'render_min_height'], 0] as any,
            'fill-extrusion-opacity': 0.88,
          },
        },
        {
          id: 'via', type: 'line' as const, source: 'openmaptiles', 'source-layer': 'transportation',
          paint: { 'line-color': '#fff', 'line-width': ['interpolate', ['linear'], ['zoom'], 10, 0.8, 18, 4] as any },
        },
        {
          id: 'limite', type: 'line' as const, source: 'openmaptiles', 'source-layer': 'boundary',
          filter: ['all', ['>=', ['to-number', ['get', 'admin_level']], 4], ['<=', ['to-number', ['get', 'admin_level']], 8]] as any,
          paint: { 'line-color': '#a78bfa', 'line-width': 1, 'line-dasharray': [3, 2] as any, 'line-opacity': 0.6 },
        },
        {
          id: 'via-nombre', type: 'symbol' as const, source: 'openmaptiles', 'source-layer': 'transportation_name',
          minzoom: 14,
          layout: {
            'symbol-placement': 'line' as const,
            'text-field': ['get', 'name'] as any, 'text-size': 11,
            'text-font': ['Noto Sans Regular'],
          },
          paint: { 'text-color': '#9ca3af', 'text-halo-color': '#fff', 'text-halo-width': 1.5 },
        },
        {
          id: 'lugar-nombre', type: 'symbol' as const, source: 'openmaptiles', 'source-layer': 'place',
          filter: ['match', ['get', 'class'], ['city', 'town', 'suburb', 'neighbourhood'], true, false] as any,
          layout: {
            'text-field': ['get', 'name'] as any, 'text-font': ['Noto Sans Bold'],
            'text-size': ['interpolate', ['linear'], ['zoom'], 4, 10, 14, 16] as any,
            'text-transform': 'uppercase' as const, 'text-letter-spacing': 0.04,
          },
          paint: { 'text-color': '#374151', 'text-halo-color': '#dde6f5', 'text-halo-width': 2 },
        },
      ] : []),
    ],
  };
}

function applyTheme(map: InstanceType<typeof maplibregl.Map>, dark: boolean) {
  const t = dark ? DARK : LIGHT;
  const safe = (fn: () => void) => { try { fn(); } catch { /* capa inexistente */ } };
  safe(() => map.setPaintProperty('fondo', 'background-color', t.fondo));
  safe(() => map.setPaintProperty('agua', 'fill-color', t.agua));
  safe(() => map.setPaintProperty('edificio-3d', 'fill-extrusion-color', t.edificio));
  safe(() => map.setPaintProperty('via', 'line-color', t.via));
  safe(() => map.setPaintProperty('limite', 'line-color', t.limite));
  safe(() => map.setPaintProperty('via-nombre', 'text-color', t.viaTexto));
  safe(() => map.setPaintProperty('via-nombre', 'text-halo-color', t.viaHalo));
  safe(() => map.setPaintProperty('lugar-nombre', 'text-color', t.lugarTexto));
  safe(() => map.setPaintProperty('lugar-nombre', 'text-halo-color', t.lugarHalo));
}

export default function MapaElParaiso({ pins = [], onPinClick, catActiva, instSlug, nocturno = false }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<InstanceType<typeof maplibregl.Map> | null>(null);
  const nocturnoRef = useRef(nocturno);
  useEffect(() => { nocturnoRef.current = nocturno; }, [nocturno]);
  const [cargando, setCargando] = useState(true);

  // Inicializar mapa una sola vez
  useEffect(() => {
    if (mapRef.current) return;
    const container = containerRef.current;
    if (!container || typeof maplibregl === 'undefined') return;

    let cancelado = false;
    let map: InstanceType<typeof maplibregl.Map> | null = null;
    let ro: ResizeObserver | null = null;

    async function iniciar() {
      let sources: Record<string, any> = {};
      let glyphs = 'https://fonts.openmaptiles.org/{fontstack}/{range}.pbf';
      try {
        const ctrl = new AbortController();
        const tid = setTimeout(() => ctrl.abort(), 6000);
        const resp = await fetch('https://tiles.openfreemap.org/styles/liberty', { signal: ctrl.signal });
        clearTimeout(tid);
        if (resp.ok) {
          const base = await resp.json();
          sources = base.sources ?? {};
          if (base.glyphs) glyphs = base.glyphs;
        }
      } catch { /* fallback a fondo plano */ }

      if (cancelado) return;

      try {
        const vista = VISTAS[instSlug ?? ''] ?? VISTAS.default;
        map = new maplibregl.Map({
          container: container!,
          style: buildStyle(sources, glyphs) as any,
          center: vista.center,
          zoom: vista.zoom,
          pitch: vista.pitch,
          attributionControl: false,
        });

        map.addControl(new maplibregl.NavigationControl(), 'bottom-right');
        map.addControl(new maplibregl.AttributionControl({ compact: true }), 'bottom-left');
        map.on('error', (e: any) => console.error('[MapLibre]', e.error));

        const vigia = setTimeout(() => { if (!cancelado) setCargando(false); }, 12000);

        map.on('load', () => {
          clearTimeout(vigia);
          if (cancelado) return;

          // Registrar imagen de emoji por categoría
          const IMG_SIZE = 52;
          Object.entries(CAT_ICONS).forEach(([slug, emoji]) => {
            if (!map!.hasImage(`pin-${slug}`)) {
              const img = makeEmojiImage(emoji, CAT_COLORS[slug] ?? '#6b7280', IMG_SIZE);
              map!.addImage(`pin-${slug}`, { width: IMG_SIZE, height: IMG_SIZE, data: img.data as any });
            }
          });

          // Fuente GeoJSON con clustering automático
          map!.addSource('pins', {
            type: 'geojson',
            data: { type: 'FeatureCollection', features: [] },
            cluster: true,
            clusterMaxZoom: 15,   // más allá de zoom 15, se expanden solos
            clusterRadius: 55,
          });

          // Capa: círculo de cluster (color por densidad)
          map!.addLayer({
            id: 'clusters',
            type: 'circle',
            source: 'pins',
            filter: ['has', 'point_count'],
            paint: {
              'circle-color': ['step', ['get', 'point_count'],
                '#22c55e', 10, '#ca8a04', 100, '#dc2626',
              ],
              'circle-radius': ['step', ['get', 'point_count'],
                22, 10, 30, 100, 40,
              ],
              'circle-stroke-width': 3,
              'circle-stroke-color': '#fff',
              'circle-opacity': 0.9,
            },
          });

          // Capa: número dentro del cluster
          map!.addLayer({
            id: 'cluster-count',
            type: 'symbol',
            source: 'pins',
            filter: ['has', 'point_count'],
            layout: {
              'text-field': '{point_count_abbreviated}',
              'text-size': 14,
              'text-font': ['Noto Sans Bold'],
            },
            paint: { 'text-color': '#fff' },
          });

          // Capa: pin individual con emoji
          map!.addLayer({
            id: 'pin-individual',
            type: 'symbol',
            source: 'pins',
            filter: ['!', ['has', 'point_count']],
            layout: {
              'icon-image': ['concat', 'pin-', ['get', 'slug']],
              'icon-size': 0.85,
              'icon-allow-overlap': true,
              'icon-anchor': 'center' as const,
            },
          });

          // Click en cluster → zoom in
          map!.on('click', 'clusters', (e: any) => {
            const features = map!.queryRenderedFeatures(e.point, { layers: ['clusters'] });
            const clusterId = features[0].properties?.cluster_id;
            const src = map!.getSource('pins') as any;
            src.getClusterExpansionZoom(clusterId, (err: any, zoom: number) => {
              if (err) return;
              map!.easeTo({ center: (features[0].geometry as any).coordinates, zoom: zoom + 0.5, duration: 500 });
            });
          });

          // Click en pin individual
          map!.on('click', 'pin-individual', (e: any) => {
            const props = e.features?.[0]?.properties;
            if (!props) return;
            onPinClick?.(props.id);

          });

          // Cursor pointer al pasar sobre clusters y pins
          ['clusters', 'pin-individual'].forEach(layer => {
            map!.on('mouseenter', layer, () => { map!.getCanvas().style.cursor = 'pointer'; });
            map!.on('mouseleave', layer, () => { map!.getCanvas().style.cursor = ''; });
          });

          applyTheme(map!, nocturnoRef.current);
          setCargando(false);
        });

        mapRef.current = map;
        ro = new ResizeObserver(() => mapRef.current?.resize());
        ro.observe(container!);
      } catch (err) {
        console.error('[MapLibre] init:', err);
        setCargando(false);
      }
    }

    iniciar();
    return () => {
      cancelado = true;
      ro?.disconnect();
      map?.remove();
      mapRef.current = null;
    };
  }, []);

  // Volar a la vista de la institución cuando cambia
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const vista = VISTAS[instSlug ?? ''] ?? VISTAS.default;
    map.flyTo({ center: vista.center, zoom: vista.zoom, pitch: vista.pitch, duration: 1200 });
  }, [instSlug]);

  // Actualizar datos del GeoJSON cuando cambian pins o filtro
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.getSource('pins')) return;
    (map.getSource('pins') as any).setData(toGeoJSON(pins, catActiva));
  }, [pins, catActiva]);

  // Propagar onPinClick actualizado sin recrear el mapa
  const onPinClickRef = useRef(onPinClick);
  useEffect(() => { onPinClickRef.current = onPinClick; }, [onPinClick]);

  // Modo nocturno — actualizar colores al cambiar el toggle
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (map.isStyleLoaded()) {
      applyTheme(map, nocturno);
    } else {
      const handler = () => applyTheme(map, nocturno);
      map.once('style.load', handler);
      return () => { map.off('style.load', handler); };
    }
  }, [nocturno]);

  return (
    <div style={{ position: 'absolute', inset: 0, background: nocturno ? '#0d1424' : '#dde6f5', transition: 'background 400ms' }}>
      <div ref={containerRef} style={{ position: 'absolute', inset: 0 }} />

      {cargando && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
          <div style={{ width: 40, height: 40, borderRadius: '50%', border: '3px solid rgba(29,78,216,0.2)', borderTopColor: '#1D4ED8', animation: 'spin 800ms linear infinite' }} />
          <div style={{ marginTop: 10, fontSize: 12, color: '#6b7280' }}>Cargando mapa…</div>
        </div>
      )}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
